<template>
  <section class="c2c-callback">
    <div v-if="phase === 'working'" class="c2c-callback-card">
      <div class="c2c-spinner" :aria-label="t('callback.completing')"></div>
      <h2>{{ t('callback.completing') }}</h2>
      <p>{{ t('callback.exchanging') }}</p>
    </div>
    <div v-else-if="phase === 'error'" class="c2c-callback-card c2c-callback-card--error">
      <h2>{{ t('callback.failed') }}</h2>
      <pre class="c2c-callback-msg">{{ errorMessage }}</pre>
      <CdxButton action="progressive" weight="primary" @click="goHome">{{ t('callback.try_again') }}</CdxButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { CdxButton } from "@wikimedia/codex"
import { handleCallback } from "@/lib/oauth"

const { t } = useI18n()
const phase = ref<"working" | "error">("working")
const errorMessage = ref("")

// Guard against double-invocation. The SPA server (serve) returns index.html
// for /oauth/callback, so the app can re-mount on the same URL while the
// first handleCallback() is still in flight (e.g. asset fetches triggering
// a reload). A second call would find the code already consumed by Wikimedia
// and fail with invalid_request. This flag is module-level so it survives
// Vue component teardown/remount within the same page session.
let callbackHandled = false

onMounted(async () => {
  if (callbackHandled) return
  callbackHandled = true
  try {
    const ok = await handleCallback()
    if (ok) window.location.replace("/")
    else window.location.replace("/")
  } catch (err) {
    phase.value = "error"
    errorMessage.value = err instanceof Error ? err.message : String(err)
  }
})

function goHome() { window.location.replace("/") }
</script>

<style scoped>
.c2c-callback {
  display: flex; justify-content: center; align-items: center; min-height: 60vh;
}
.c2c-callback-card {
  text-align: center; background: #f8f9fa;
  border: 1px solid #eaecf0; border-radius: 8px;
  padding: 32px; max-width: 480px;
}
.c2c-callback-card--error { background: #fee7e6; border-color: #d73333; }
.c2c-callback h2 { margin: 12px 0 8px 0; font-size: 20px; }
.c2c-callback p { margin: 0; color: #54595d; }
.c2c-callback-msg {
  background: white; border: 1px solid #d8d8d8; border-radius: 3px;
  padding: 12px; margin: 12px 0 16px 0;
  white-space: pre-wrap; word-break: break-word;
  font: 13px/1.5 ui-monospace, monospace;
  text-align: start; max-height: 200px; overflow-y: auto;
}
.c2c-spinner {
  width: 40px; height: 40px;
  border: 3px solid #c8ccd1; border-top-color: #3366cc;
  border-radius: 50%; margin: 0 auto;
  animation: c2c-spin 0.8s linear infinite;
}
@keyframes c2c-spin { to { transform: rotate(360deg); } }
</style>